import { useCallback, useEffect, useRef, useState } from 'react';
import { useAxiosSwagger } from './useAxiosSwagger';
import { describeError } from '../api/requeue';
import {
    FIND_MAX_PAGES,
    FindItem,
    FindResult,
    PAYMENTS_CHUNK_SIZE,
    REPLAY_CHUNK_SIZE,
    ReplayKind,
    ReplayResult,
    find,
    paymentsOfInvoices,
    replay,
} from '../api/replay';

export interface ReplayProgress {
    done: number;
    total: number;
}

/** What a running search has turned up so far. */
export interface FindState {
    kind: ReplayKind;
    scanned: number;
    inExact: number;
    missing: number;
    notIssued: number;
    items: FindItem[];
    /** Where Chargebee would continue, when there is more. */
    nextOffset: string | null;
    /** Whether the browser stopped itself rather than reaching the end. */
    stoppedEarly: boolean;
    /**
     * The ids the search could say nothing about, when it started from a list. Empty for a
     * date walk, which has no list to be silent about.
     */
    unanswered: string[];
    /**
     * What the search started from, when it started from a list of invoices. Kept so the
     * result can say "de betalingen bij deze 12 facturen" rather than just a count.
     */
    ofInvoices: number;
}

export interface ReplayMonitor {
    /** What the last run turned up, dry or real. */
    result: ReplayResult | null;
    /** Whether that result came from a dry run, so the panel knows what to offer next. */
    checkedOnly: boolean;
    /**
     * The kind the result is about. Not always the kind picked in the panel: the trace starts a
     * fetch for the object on screen, and offering has to follow what was fetched.
     */
    resultKind: ReplayKind | null;
    busy: boolean;
    progress: ReplayProgress | null;
    error: string | null;
    /** Fetches every id from Chargebee and shows what would happen, without writing anything. */
    check: (kind: ReplayKind, ids: string[]) => Promise<ReplayResult | null>;
    /** Fetches the given ids again and offers them to the connector. */
    run: (kind: ReplayKind, ids: string[]) => Promise<ReplayResult | null>;
    reset: () => void;

    /** What the search over Chargebee has turned up, or null when none has run. */
    found: FindState | null;
    /** Whether a search is walking pages right now. */
    searching: boolean;
    /** Which page of Chargebee is being read, for the reader watching it climb. */
    searchedPages: number;
    /** Walks Chargebee from the start of the period. */
    search: (
        kind: ReplayKind,
        from: Date | null,
        to: Date | null,
        skipNotIssued: boolean
    ) => Promise<void>;
    /** Picks the walk up where it stopped. */
    searchMore: () => Promise<void>;
    /**
     * Which payments Chargebee has against these invoices, and which of those are missing.
     *
     * The step after fetching invoices back: a replayed invoice does not bring its payments
     * along. Lands in the same `found` state as a date walk, so the same "zet deze in de
     * lijst" button carries them into the ordinary flow.
     */
    searchPayments: (
        invoiceIds: string[],
        onlySucceeded: boolean
    ) => Promise<void>;
    /** Abandons a running search, keeping what it already found. */
    stopSearch: () => void;
    clearSearch: () => void;
}

/** A search that has found nothing yet, of one kind. */
const emptyFound = (kind: ReplayKind): FindState => ({
    kind,
    scanned: 0,
    inExact: 0,
    missing: 0,
    notIssued: 0,
    items: [],
    nextOffset: null,
    stoppedEarly: false,
    unanswered: [],
    ofInvoices: 0,
});

/**
 * Fetching objects back out of Chargebee, in two steps.
 *
 * The two steps are the point. A list of invoice numbers pasted out of a spreadsheet is
 * exactly the input nobody has checked, and the answers that matter - does Chargebee still
 * have this, is it already in Exact - are only knowable by asking. So the panel checks
 * first, shows what it found, and only then offers to write anything.
 */
export const useReplay = (
    customer?: string,
    environment?: string
): ReplayMonitor => {
    const axios = useAxiosSwagger();

    const [result, setResult] = useState<ReplayResult | null>(null);
    const [checkedOnly, setCheckedOnly] = useState<boolean>(true);
    const [resultKind, setResultKind] = useState<ReplayKind | null>(null);
    const [busy, setBusy] = useState<boolean>(false);
    const [progress, setProgress] = useState<ReplayProgress | null>(null);
    const [error, setError] = useState<string | null>(null);

    const mounted = useRef<boolean>(true);

    useEffect(() => {
        mounted.current = true;

        return () => {
            mounted.current = false;
        };
    }, []);

    // A result is about one environment; switching has to clear it rather than leave a list
    // of invoice numbers from somewhere else on screen next to a button that writes.
    useEffect(() => {
        setResult(null);
        setError(null);
        setProgress(null);
    }, [customer, environment]);

    const send = useCallback(
        async (kind: ReplayKind, ids: string[], dryRun: boolean) => {
            if (!axios || !customer || !environment || ids.length === 0) {
                return null;
            }

            setBusy(true);
            setError(null);
            setResult(null);
            setCheckedOnly(dryRun);
            setResultKind(kind);
            setProgress({ done: 0, total: ids.length });

            const aggregate: ReplayResult = {
                queueName: '',
                dryRun,
                requested: 0,
                unique: 0,
                queued: 0,
                skipped: 0,
                failed: 0,
                results: [],
            };

            try {
                // In blocks, because the API refuses a request over its ceiling rather than
                // doing part of it - and because every id is a call to Chargebee, so a long
                // list without any sign of progress looks exactly like a hung request.
                for (let at = 0; at < ids.length; at += REPLAY_CHUNK_SIZE) {
                    const chunk = ids.slice(at, at + REPLAY_CHUNK_SIZE);

                    // eslint-disable-next-line no-await-in-loop
                    const response = await replay(axios, customer, environment, {
                        kind,
                        ids: chunk,
                        dryRun,
                    });

                    if (response.error) {
                        throw new Error(response.error);
                    }

                    aggregate.queueName = response.queueName;

                    // The same sentence about the same environment on every block, so the
                    // last one wins rather than being appended a dozen times.
                    aggregate.warning = response.warning ?? aggregate.warning;

                    aggregate.requested += response.requested;
                    aggregate.unique += response.unique;
                    aggregate.queued += response.queued;
                    aggregate.skipped += response.skipped;
                    aggregate.failed += response.failed;
                    aggregate.results.push(...response.results);

                    if (!mounted.current) {
                        return aggregate;
                    }

                    setProgress({
                        done: Math.min(at + chunk.length, ids.length),
                        total: ids.length,
                    });

                    setResult({
                        ...aggregate,
                        results: [...aggregate.results],
                    });
                }

                return aggregate;
            } catch (ex) {
                if (mounted.current) {
                    setError(describeError(ex));
                }

                // Whatever was already done stays visible: on a real run those objects are
                // on the queue whether or not the rest of the list made it.
                if (aggregate.results.length === 0) {
                    return null;
                }

                if (mounted.current) {
                    setResult({
                        ...aggregate,
                        results: [...aggregate.results],
                    });
                }

                return aggregate;
            } finally {
                if (mounted.current) {
                    setBusy(false);
                    setProgress(null);
                }
            }
        },
        [axios, customer, environment]
    );

    const check = useCallback(
        (kind: ReplayKind, ids: string[]) => send(kind, ids, true),
        [send]
    );

    const run = useCallback(
        (kind: ReplayKind, ids: string[]) => send(kind, ids, false),
        [send]
    );

    const reset = useCallback(() => {
        setResult(null);
        setError(null);
        setProgress(null);
        setCheckedOnly(true);
    }, []);

    const [found, setFound] = useState<FindState | null>(null);
    const [searching, setSearching] = useState<boolean>(false);
    const [searchedPages, setSearchedPages] = useState<number>(0);

    /** Set while a walk runs; cleared to make it stop after the page it is on. */
    const walking = useRef<boolean>(false);

    /** What the current walk was asked for, so continuing asks the same thing. */
    const criteria = useRef<{
        kind: ReplayKind;
        from: Date | null;
        to: Date | null;
        skipNotIssued: boolean;
    } | null>(null);

    useEffect(() => {
        setFound(null);
    }, [customer, environment]);

    /**
     * Walks Chargebee page by page, adding to what is already on screen.
     *
     * The pages are a chain - each answer names the offset of the next - so they cannot be
     * fetched at once, and the walk shows its progress instead. It stops on its own after
     * FIND_MAX_PAGES rather than running until Chargebee runs out: at that point somebody
     * should be deciding whether this period was the right one.
     */
    const walk = useCallback(
        async (from: FindState | null, startOffset: string | null) => {
            if (!axios || !customer || !environment || !criteria.current) {
                return;
            }

            const asked = criteria.current;

            walking.current = true;
            setSearching(true);
            setError(null);

            let state: FindState = from ?? emptyFound(asked.kind);

            let offset = startOffset;
            let pages = 0;

            try {
                do {
                    // eslint-disable-next-line no-await-in-loop
                    const page: FindResult = await find(
                        axios,
                        customer,
                        environment,
                        {
                            kind: asked.kind,
                            from: asked.from,
                            to: asked.to,
                            offset,
                            onlyMissing: true,
                            skipNotIssued: asked.skipNotIssued,
                        }
                    );

                    pages += 1;

                    state = {
                        ...state,
                        scanned: state.scanned + page.scanned,
                        inExact: state.inExact + page.inExact,
                        missing: state.missing + page.missing,
                        notIssued: state.notIssued + page.notIssued,
                        items: [...state.items, ...page.items],
                        nextOffset: page.nextOffset ?? null,
                        stoppedEarly: false,
                    };

                    offset = page.nextOffset ?? null;

                    if (!mounted.current) {
                        return;
                    }

                    setFound(state);
                    setSearchedPages(pages);

                    // A page that failed still carries the offset it reached, so the walk
                    // stops here and the button offers to pick it up rather than restart.
                    if (page.error) {
                        setError(page.error);

                        return;
                    }

                    if (pages >= FIND_MAX_PAGES && offset) {
                        setFound({ ...state, stoppedEarly: true });

                        return;
                    }
                } while (offset && walking.current);

                if (mounted.current && offset && !walking.current) {
                    setFound({ ...state, stoppedEarly: true });
                }
            } catch (ex) {
                if (mounted.current) {
                    setError(describeError(ex));
                    setFound({ ...state, stoppedEarly: true });
                }
            } finally {
                walking.current = false;

                if (mounted.current) {
                    setSearching(false);
                }
            }
        },
        [axios, customer, environment]
    );

    const search = useCallback(
        async (
            kind: ReplayKind,
            from: Date | null,
            to: Date | null,
            skipNotIssued: boolean
        ) => {
            criteria.current = { kind, from, to, skipNotIssued };

            setFound(null);
            setSearchedPages(0);

            await walk(null, null);
        },
        [walk]
    );

    const searchMore = useCallback(async () => {
        if (!found?.nextOffset) {
            return;
        }

        await walk(found, found.nextOffset);
    }, [found, walk]);

    /**
     * The payments Chargebee has against a list of invoices.
     *
     * In blocks, because the API refuses a request over its ceiling rather than doing part of
     * it, and because every invoice is a call to Chargebee - a hundred of them without a
     * counter climbing looks exactly like a hung request. There is no offset to continue
     * from here: the list of invoices is the whole question, so once it is walked the answer
     * is complete.
     */
    const searchPayments = useCallback(
        async (invoiceIds: string[], onlySucceeded: boolean) => {
            if (!axios || !customer || !environment || invoiceIds.length === 0) {
                return;
            }

            criteria.current = null;
            walking.current = true;

            setSearching(true);
            setSearchedPages(0);
            setError(null);
            setFound(null);

            let state: FindState = {
                ...emptyFound('Payment'),
                ofInvoices: invoiceIds.length,
            };

            try {
                for (
                    let at = 0;
                    at < invoiceIds.length && walking.current;
                    at += PAYMENTS_CHUNK_SIZE
                ) {
                    const chunk = invoiceIds.slice(
                        at,
                        at + PAYMENTS_CHUNK_SIZE
                    );

                    // eslint-disable-next-line no-await-in-loop
                    const page = await paymentsOfInvoices(
                        axios,
                        customer,
                        environment,
                        chunk,
                        onlySucceeded
                    );

                    state = {
                        ...state,
                        scanned: state.scanned + page.scanned,
                        inExact: state.inExact + page.inExact,
                        missing: state.missing + page.missing,
                        notIssued: state.notIssued + page.notIssued,
                        items: [...state.items, ...page.items],
                        unanswered: [
                            ...state.unanswered,
                            ...(page.unanswered ?? []),
                        ],
                    };

                    if (!mounted.current) {
                        return;
                    }

                    setFound(state);
                    setSearchedPages(
                        Math.ceil(
                            Math.min(at + chunk.length, invoiceIds.length) /
                                PAYMENTS_CHUNK_SIZE
                        )
                    );

                    // A block that failed still carries what it read. Stopping here rather
                    // than pressing on keeps a rate limit from turning the rest of the list
                    // into "geen betalingen gevonden".
                    if (page.error) {
                        setError(page.error);

                        return;
                    }
                }
            } catch (ex) {
                if (mounted.current) {
                    setError(describeError(ex));
                    setFound(state);
                }
            } finally {
                walking.current = false;

                if (mounted.current) {
                    setSearching(false);
                }
            }
        },
        [axios, customer, environment]
    );

    const stopSearch = useCallback(() => {
        walking.current = false;
    }, []);

    const clearSearch = useCallback(() => {
        walking.current = false;

        setFound(null);
        setSearchedPages(0);
    }, []);

    return {
        result,
        checkedOnly,
        resultKind,
        busy,
        progress,
        error,
        check,
        run,
        reset,
        found,
        searching,
        searchedPages,
        search,
        searchMore,
        searchPayments,
        stopSearch,
        clearSearch,
    };
};
